/**
 * Copy for Capture (DCI), transcribed from the Figma "Upload Flow" section
 * (9856:16314). Error wording follows the DCI spec §28 table the guide cites;
 * the server's `detail.code` picks the line, never its free-text message.
 *
 * Two design elements are deliberately not transcribed (v26.5):
 * - "I've created a Guided Journey" / "Why a Guided Journey?" — Guided
 *   Journey and Guided Conversation are retired as member-facing classes
 *   (Unified Dialogue v26.3 §3); there is one object, a Dialogue.
 * - "Make This My Current Focus?" — Current Focus is retired and its endpoint
 *   is deleted.
 */

export const HUB_COPY = {
  title: "Capture",
  question: "How would you like to bring trusted knowledge into Aris?",
  hint: "Choose the option that works best.",
  private: "Your data is private and secure.",
  unavailable:
    "Capture isn't available on your account yet. When it is, you'll be able to turn conversations, documents, photos and notes into Dialogues made for you.",
  methods: {
    RECORDING: { title: "Record Live", body: "Record a conversation or session." },
    DOCUMENT: { title: "Upload a Document", body: "Upload a PDF, Word document, PowerPoint, or any file." },
    PHOTO: {
      title: "Upload a Photo",
      body: "Take or upload a photo of notes, whiteboard, or any written material.",
    },
    TEXT: { title: "Type or Paste Text", body: "Type your notes or paste text you want Aris to understand." },
  },
};

export const RECORD_COPY = {
  title: "Record Live",
  readyTitle: "Ready when you are",
  readyBody: "Tap start and speak naturally. Aris is listening and will create a personalized experience for you.",
  start: "Start Recording",
  recording: "Recording...",
  recordingBody: "Aris is listening and will create a personalized experience for you.",
  stop: "Stop Recording",
  stopping: "Finishing up…",
  cancel: "Cancel",
  warning: (minutes) => `${minutes} minutes of recording left.`,
  limit: "You've reached the recording limit. Aris is saving what you recorded.",
  consentTitle: "Before you record",
  consentBody:
    "Make sure everyone in the conversation knows you're recording and agrees. Aris turns the recording into a transcript and then deletes the audio.",
  consentAccept: "I understand",
  micDenied: "Aris needs access to your microphone to record. Allow it in your browser settings and try again.",
  recovering: "The connection dropped. Saving your recording…",
  lost: "The connection dropped and the recording couldn't be saved. Please try again.",
};

export const DOCUMENT_COPY = {
  title: "Upload a Document",
  drop: "Drag and drop your file here",
  browse: "or tap to browse",
  types: "PDF, Word, PowerPoint, or any file",
  browseCta: "Browse Files",
  continue: "Continue",
  remove: "Remove file",
  longRead: (minutes) =>
    `This is a long document — analysis may take about ${minutes} minutes. You don't need to stay on this screen.`,
};

export const PHOTO_COPY = {
  title: "Upload a Photo",
  prompt: "Take or upload photo of your notes",
  take: "Take Photo",
  library: "Choose from Library",
  continue: "Continue with photo",
  continueMany: (n) => `Continue with ${n} photos`,
  retake: "Take new photo",
  addAnother: "Add another photo",
  remove: "Remove photo",
  max: "You can add up to 10 photos.",
};

export const TEXT_COPY = {
  title: "Type or Paste Text",
  placeholder: "Add any notes, insights, or information you want Aris to understand",
  continue: "Continue",
  counter: (n, max) => `${n.toLocaleString()} / ${max.toLocaleString()}`,
};

/** Analyzing (spec §2 table): one label per `progress_step`, 1–5. */
export const ANALYZING_COPY = {
  title: "Analyzing...",
  lead: "Aris is understanding your input...",
  steps: [
    "Processing content",
    "Extracting key insights",
    "Identifying actions and commitments",
    "Understanding context",
    "Creating your personalized dialogue",
  ],
  hint: "This usually takes less than a minute",
  slow: "This is taking a little longer than usual. Aris will let you know when it's ready.",
  pages: (done, total) => `${done} of ${total} pages read`,
};

export const SOURCE_COPY = {
  title: "Confirm Source",
  lead: "Aris identified the source and details below. Please confirm or edit if needed.",
  sourceType: "SOURCE",
  providedBy: "PROVIDED BY",
  relationship: "RELATIONSHIP",
  titleLabel: "TITLE (OPTIONAL)",
  notSet: "Add",
  confirm: "Looks Good",
  sheets: {
    source_role: "Source",
    source_category: "Source",
    provider_name: "Provided by",
    relationship: "Relationship",
    title: "Title (Optional)",
    other: "Describe it",
  },
  search: "Search",
  cancel: "Cancel",
  update: "Update",
};

export const SUMMARY_COPY = {
  title: "Summary & Insights",
  lead: "Review what Aris captured from your input.",
  summary: "SUMMARY",
  takeaways: "KEY TAKEAWAYS",
  actions: "ACTION & COMMITMENTS",
  verify: "Please verify",
  confirm: "Looks Good",
};

export const CREATED_COPY = {
  title: "I've created a Dialogue for you.",
  body: "It's built from what you shared and shaped around your life, so you can work through it with Aris at your own pace.",
  continue: "Continue",
};

export const READY_COPY = {
  title: "You're all set!",
  body: "Your personalized experience is ready to help you grow.",
  checks: ["Dialogue created", "Saved to Trusted Knowledge", "Integrated into Aris"],
  cta: "Go to My Dialogue",
  datesTitle: "Dates from your source",
  datesLead: "Want a reminder? Aris only sets one when you say so.",
  remind: "Remind me",
  reminded: "Reminder set",
  undo: "Undo",
  savedNoPush: "Reminder saved",
  verifyDate: "Needs your check",
};

/** Failure copy, keyed by `error_code` (DCI spec §28). */
export const FAILURE_COPY = {
  title: "Something needs your attention",
  NOT_ENOUGH_INFORMATION:
    "I need a little more information to create something useful. Try adding more detail and start again.",
  UNREADABLE_PHOTO: "I can't read enough of this image clearly. Try retaking it or choosing another.",
  DIALOGUE_GENERATION_FAILED: "I understood your input, but I couldn't create the dialogue yet.",
  SAFETY_HOLD:
    "What you shared sounds important. If you're in crisis or thinking about harming yourself, please reach out to someone now — in the US you can call or text 988.",
  terminal: "Analysis failed. Please start over.",
  retry: "Try Again",
  startOver: "Start Over",
  close: "Close",
};

/** Upload / request refusals, keyed by `detail.code`. */
export const ERROR_COPY = {
  UNSUPPORTED_FILE: "That file type isn't supported. Try a PDF, Word, PowerPoint, text or image file.",
  FILE_TOO_LARGE: "That file is too large.",
  PASSWORD_PROTECTED_FILE: "That file is password-protected. Remove the password and try again.",
  DOCUMENT_TOO_LONG: "That document is over 300 pages. Try a shorter one.",
  CORRUPT_FILE: "That file couldn't be read. It may be damaged.",
  TEXT_TOO_LONG: "That's more text than Aris can take in at once (50,000 characters).",
  TOO_MANY_IMAGES: "You can add up to 10 photos.",
  NOT_ENOUGH_INFORMATION: "Add something for Aris to work with first.",
  MALWARE_DETECTED: "One of the files didn't pass the safety check and was removed.",
  DAILY_LIMIT_REACHED: "You've reached today's limit for new captures. Try again tomorrow.",
  STAGE_CONFLICT: "This step has already moved on. Refreshing…",
  default: "Something went wrong. Please try again.",
};

export const errorText = (code) => ERROR_COPY[code] || ERROR_COPY.default;
